import { useTranslation } from 'react-i18next'
import { Check, X } from 'lucide-react'
import type { MarketKey, QuotedGroup, QuotedMarket, QuotedOutcome } from '@brochure/shared'
import { outcomeSide, type Side } from '@/lib/zambebet'
import { cn } from '@/lib/utils'
import { SideMark } from '@/components/zambepong/SideMark'
import { OddsFlaps } from './MarketCard'
import { useOutcomeLabel } from './useOutcomeLabel'

// Risultato esatto: tre per riga da tablet in su; bicchieri del giocatore: le quattro linee in fila da tablet.
// Mercati col nome del giocatore: una colonna sul telefono, così "Francesco" non va a capo a metà.
// Il resto va a coppie (casa/ospite, sì/no, over/under), ma una per riga sotto i 360 px: "Over 2,5" resta intero.
const PAIRS = 'grid-cols-1 min-[360px]:grid-cols-2'
const COLUMNS: Partial<Record<MarketKey, string>> = {
  exact_score: 'grid-cols-2 sm:grid-cols-3',
  player_cups: 'grid-cols-1 min-[360px]:grid-cols-2 sm:grid-cols-4',
  top_scorer: 'grid-cols-1 min-[420px]:grid-cols-2',
  first_scorer: 'grid-cols-1 min-[420px]:grid-cols-2',
  outright_winner: 'grid-cols-1 min-[420px]:grid-cols-2',
  outright_finalist: 'grid-cols-1 min-[420px]:grid-cols-2',
  outright_top_scorer: 'grid-cols-1 min-[420px]:grid-cols-2',
}

/** Esiti dei bicchieri del giocatore divisi per giocatore, nell'ordine in cui arrivano. */
const byPlayer = (outcomes: QuotedOutcome[]): [string, QuotedOutcome[]][] => {
  const groups = new Map<string, QuotedOutcome[]>()
  for (const o of outcomes) groups.set(o.subjectId ?? '', [...(groups.get(o.subjectId ?? '') ?? []), o])
  return [...groups]
}

type MatchMarketsProps = {
  /** I mercati del gruppo scelto (la pagina chiede un gruppo per volta). */
  markets: QuotedMarket[]
  group: QuotedGroup
  /** `home`/`away` → nomi delle squadre, id → nomi dei giocatori. */
  names: Record<string, string>
  /** Id giocatore → lato: squadre e giocatori portano la striscia di casa (azzurra) o trasferta (corallo). */
  sides: Record<string, Side>
  /** Ospite iscritto e partita giocabile: ogni esito aperto e quotato entra (o esce) dalla schedina. */
  canPick: boolean
  isPicked: (o: QuotedOutcome) => boolean
  onPick: (o: QuotedOutcome) => void
}

/**
 * Mercati di un gruppo di una partita come un palinsesto da bookmaker. Ogni esito è una casella con il nome a sinistra e la quota sulle
 * palette a destra; gli esiti decisi dal vivo mostrano spunta o croce al posto della quota. Toccare un esito lo mette in schedina.
 */
export function MatchMarkets({ markets, group, names, sides, canPick, isPicked, onPick }: MatchMarketsProps) {
  const { t } = useTranslation()
  const label = useOutcomeLabel()

  const outcomeCell = (market: QuotedMarket, o: QuotedOutcome) => {
    const clickable = canPick && market.status === 'open' && o.resolution === 'open' && o.odds !== null
    const picked = isPicked(o)
    // Nei bicchieri del giocatore il lato sta già sull'intestazione col nome.
    const side = market.key === 'player_cups' ? null : outcomeSide(o, sides)
    return (
      <button
        key={`${o.selection}-${o.line ?? ''}-${o.subjectId ?? ''}`}
        type="button"
        disabled={!clickable}
        aria-pressed={picked}
        onClick={() => onPick(o)}
        data-testid={`outcome-${market.key}-${o.subjectId ? `${o.subjectId}-` : ''}${o.selection}${o.line !== undefined ? `-${o.line}` : ''}`}
        className={cn(
          'flex min-h-12 min-w-0 items-center justify-between gap-2 rounded-lg border px-2.5 py-1.5 text-left transition-[transform,border-color,background-color] duration-200 ease-out-expo',
          'border-paper/12 bg-paper/5 enabled:hover:border-paper/45 enabled:active:scale-[0.97] disabled:cursor-default',
          o.resolution === 'won' && 'border-sun/60 bg-sun/10',
          o.resolution === 'lost' && 'opacity-45',
          picked && 'border-sun bg-sun/15',
        )}
      >
        <span className="flex min-w-0 items-center gap-2">
          {side && <SideMark side={side} />}
          <span className={cn('line-clamp-2 min-w-0 break-words font-body text-xs font-semibold leading-snug text-paper/80', o.resolution === 'lost' && 'line-through')}>
            {label(o, names)}
          </span>
        </span>
        <span className="shrink-0">
          {o.resolution === 'won' && <Check className="size-4 text-sun" role="img" aria-label={t('zambebet.resolution.won')} />}
          {o.resolution === 'lost' && <X className="size-4 text-paper/60" role="img" aria-label={t('zambebet.resolution.lost')} />}
          {o.resolution === 'open' && o.odds !== null && <OddsFlaps odds={o.odds} className="text-sm text-paper" />}
          {o.resolution === 'open' && o.odds === null && <span className="font-body text-xs text-paper/45">–</span>}
        </span>
      </button>
    )
  }

  const marketBlock = (market: QuotedMarket) => (
    <section key={market.key} data-testid={`market-${market.key}`} aria-labelledby={`market-title-${market.key}`} className="py-4 first:pt-1">
      <h3 id={`market-title-${market.key}`} className="flex items-baseline justify-between gap-3 font-body text-sm font-semibold text-paper">
        {t(`zambebet.marketNames.${market.key}`)}
        {market.status !== 'open' && <span className="text-xs font-medium text-paper/50">{t(`zambebet.marketStatus.${market.status}`)}</span>}
      </h3>
      {market.key === 'player_cups' ? (
        byPlayer(market.outcomes).map(([playerId, outcomes]) => (
          <div key={playerId} className="mt-3">
            <p className="flex items-center gap-2 font-body text-xs font-semibold text-paper/70">
              {sides[playerId] && <SideMark side={sides[playerId]} className="h-3" />}
              {names[playerId]}
            </p>
            <div className={cn('mt-1.5 grid gap-1.5', COLUMNS.player_cups)}>{outcomes.map((o) => outcomeCell(market, o))}</div>
          </div>
        ))
      ) : (
        <div className={cn('mt-2 grid gap-1.5', COLUMNS[market.key] ?? PAIRS)}>{market.outcomes.map((o) => outcomeCell(market, o))}</div>
      )}
    </section>
  )

  return (
    <section data-testid={`market-group-${group}`} className="divide-y divide-paper/10 rounded-xl border border-paper/12 bg-paper/[0.03] px-3 pt-3 pb-1 sm:px-4">
      {markets.map(marketBlock)}
    </section>
  )
}
